<script setup lang="ts">
import { useTaStore } from '#trade-activity/stores/analyze-trade-activity'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { NoSearchResults } from '@forgd/ui-kit/blocks/empty-states'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@forgd/ui-kit/combobox'
import { useQuery } from '@pinia/colada'

type CoinListItem = SuccessResponseBody<'/business-intelligence/coins'>[number]

const store = useTaStore()

const { open, query, debouncedQuery, filtered, loading, reset } = useAsyncComboboxFilter<CoinListItem>(
  async (q) => {
    const result = await apiFetch('/business-intelligence/coins', {
      params: {
        query: { search: q, limit: 50 },
      },
    })
    return result ?? []
  },
)

// Popularity-ordered default list, shown before the user types a search query
const defaultCoinsQuery = useQuery({
  ...buildApiQuery('/business-intelligence/coins'),
  refetchOnWindowFocus: false,
})

const isSearching = computed(() => debouncedQuery.value.length >= 2)
const options = computed<CoinListItem[]>(() => {
  if (isSearching.value) {
    return filtered.value
  }
  return defaultCoinsQuery.data.value ?? []
})

function handleSelect(coin: CoinListItem) {
  store.selectCoin(coin)
  reset()
}
</script>

<template>
  <Combobox v-model:open="open">
    <ComboboxTrigger size="sm" class="w-full" data-testid="ta-token-select">
      <div class="flex min-w-0 items-center gap-1">
        <ProjectAvatar :src="store.displayImage" size="xs" />
        <span class="truncate text-xs font-semibold text-primal">{{ store.displayTicker }}</span>
      </div>
    </ComboboxTrigger>
    <ComboboxContent>
      <ComboboxInput
        v-model="query"
        :loading="loading"
        type="search"
        role="searchbox"
        placeholder="Write name or ticker..."
      />
      <ComboboxList>
        <ComboboxEmpty
          v-if="loading || (!isSearching && defaultCoinsQuery.isPending.value)"
          class="items-start justify-start px-2 py-1.5 text-left text-placeholder"
        >
          Loading...
        </ComboboxEmpty>
        <ComboboxEmpty v-else-if="options.length === 0" class="py-0">
          <NoSearchResults
            title="No results"
            message="Please modify your search"
            @clear="query = ''"
          />
        </ComboboxEmpty>
        <template v-else>
          <ComboboxItem
            v-for="(opt, index) in options"
            :key="opt.id"
            :value="opt.id"
            :data-testid="`ta-token-select-option-${index}`"
            :selected="store.activeCoin?.id === opt.id"
            @select="handleSelect(opt)"
          >
            <div class="flex min-w-0 items-center gap-2">
              <ProjectAvatar :src="opt.image" size="sm" />
              <span class="text-xs font-semibold text-primal uppercase">{{ opt.symbol }}</span>
              <span class="truncate text-xs text-secondary">{{ opt.name }}</span>
            </div>
          </ComboboxItem>
        </template>
      </ComboboxList>
    </ComboboxContent>
  </Combobox>
</template>
